import React from 'react';

// ══════════════════════════════════════════════════════════════════════
//  CLASS CHOICE PAIR  —  a Schedule day with exactly two classes
//
//  Design: docs/ux/class-choice-preview.html, Option B (chosen by the owner
//  2026-10-13). The two tiles sit together in one soft card, side by side on
//  EVERY screen size (the normal Schedule grid is one column on a phone, which
//  would turn "Classic left, Strong+ right" into top/bottom), with a small
//  serif "or" between them. The card's own heading says what to do.
//
//  "Next up": when the Schedule's spotlight lands on this day, the pink frame
//  and the eyebrow wrap the PAIR, not one tile — framing only the left tile
//  would tell members the Classic is "the" class and the other an extra.
//
//  Purely presentational: the two tiles arrive fully built (children), with
//  every click target, CTA and access rule untouched. Ordering (Classic left,
//  Strong+ right) is decided by the caller.
//
//  Production theme trap: --mom-warm-gray === --mom-charcoal, so the muted
//  tones below are color-mixed (see ClassGuide.js / reference_live_theme_values).
// ══════════════════════════════════════════════════════════════════════

const MUTED = 'color-mix(in srgb, var(--mom-charcoal) 60%, var(--mom-cream))';
const LABEL_TONE = 'color-mix(in srgb, var(--mom-charcoal) 78%, var(--mom-cream))';
const RULE = 'color-mix(in srgb, var(--mom-charcoal) 13%, var(--mom-cream))';

/**
 * @param {string}  spotlightEyebrow  non-empty ⇒ this pair is the Schedule's "Next up"
 * @param {string}  kicker            small caps label when NOT the spotlight
 * @param {string}  heading           the italic instruction line
 * @param {boolean} isMobile          ≤600px — tighter padding + type
 * @param {Array}   children          exactly two rendered tiles, left then right
 */
export default function ClassChoicePair({ spotlightEyebrow = '', kicker, heading, isMobile = false, children }) {
    const [left, right] = React.Children.toArray(children);
    const spot = !!spotlightEyebrow;

    const frame = spot
        ? {
            // Gradient border with a soft cream fill — the same pink frame the
            // single "Next up" tile uses, drawn around the pair instead.
            border: '2px solid transparent',
            background: 'linear-gradient(#fbf7f4, #fbf7f4) padding-box, linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark)) border-box',
            boxShadow: '0 8px 28px color-mix(in srgb, var(--mom-primary) 18%, transparent)',
        }
        : {
            border: '1px solid rgba(255,255,255,0.75)',
            background: 'rgba(255,255,255,0.5)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.035)',
        };

    return (
        <div style={{ borderRadius: 20, padding: isMobile ? 12 : 16, ...frame }}>
            <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                gap: isMobile ? 4 : 10, flexWrap: 'wrap',
                margin: isMobile ? '0 2px 10px' : '0 2px 12px',
            }}>
                <span style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.13em', textTransform: 'uppercase',
                    color: spot ? 'var(--mom-primary-dark)' : MUTED,
                }}>
                    {spot ? spotlightEyebrow : kicker}
                </span>
                <span style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic',
                    fontSize: isMobile ? 13.5 : 15, color: LABEL_TONE, lineHeight: 1.3,
                }}>
                    {heading}
                </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', alignItems: 'stretch' }}>
                {left}
                {/* "or" medallion — overlaps the gap between the tiles so the two
                    read as alternatives, not as a list of things to do. */}
                <div aria-hidden="true" style={{
                    alignSelf: 'center', zIndex: 2,
                    width: isMobile ? 26 : 30, height: isMobile ? 26 : 30,
                    margin: isMobile ? '0 -6px' : '0 -7px',
                    borderRadius: '50%', background: 'var(--mom-cream)',
                    border: `1px solid ${RULE}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic',
                    fontSize: isMobile ? 12 : 13, color: MUTED,
                }}>
                    or
                </div>
                {right}
            </div>
        </div>
    );
}
